import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { searchItemsApi } from "../../services/searchService";
import { SearchItemsParams, SearchState } from "../../types";
import { RootState } from "../store";
import { resolveLocation } from "../../utils/locationHelper";
import { getLocationFromContext, getUserIdFromContext } from "../../utils/apiUtils";
import { normalizeSelectedIds } from "../../utils/formatters";

const initialState: SearchState = {
  searchQuery: "",
  searchInputValue: "",
  isSearchFilterOpen: false,
  searchResults: [],
  debouncedSearchResults: [],
  loading: false,
  error: null,
  selectedCategory: [],
  selectedCondition: [],
  maxDistance: 50,
};

export const searchItems = createAsyncThunk(
  "search/searchItems",
  async (
    {
      lat,
      lon,
      keyword,
      radiusInKm,
      itemCondition,
      itemCategory,
      sortKey,
      itemsPerPage,
      pageNo,
    }: SearchItemsParams,
    thunkAPI
  ) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const userId = getUserIdFromContext(state);
      const locationState = getLocationFromContext(state);
      const resolvedLoc = resolveLocation({
        lat,
        lon,
        locationState,
        allowNull: true,
      });
      if (resolvedLoc.error) {
        return thunkAPI.rejectWithValue(resolvedLoc.error);
      }
      const response = await searchItemsApi({
        lat: resolvedLoc.lat,
        lon: resolvedLoc.lon,
        userId: userId ?? undefined,
        keyword,
        radiusInKm,
        itemCondition: normalizeSelectedIds(itemCondition),
        itemCategory: normalizeSelectedIds(itemCategory),
        sortKey,
        itemsPerPage,
        pageNo,
      });
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Search failed");
    }
  }
);

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.searchInputValue = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
      state.searchInputValue = "";
      state.isSearchFilterOpen = false;
      state.searchResults = [];
      state.debouncedSearchResults = [];
      state.selectedCategory = [];
      state.selectedCondition = [];
      state.maxDistance = 50;
    },
    setSearchFilterOpen: (state, action: PayloadAction<boolean>) => {
      state.isSearchFilterOpen = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<string[]>) => {
      state.selectedCategory = action.payload;
    },
    setSelectedCondition: (state, action: PayloadAction<string[]>) => {
      state.selectedCondition = action.payload;
    },
    setMaxDistance: (state, action: PayloadAction<number>) => {
      state.maxDistance = Math.max(10, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchItems.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.searchResults = Array.isArray(payload)
          ? payload
          : (payload?.items && Array.isArray(payload.items) ? payload.items : []);
        const seenNames = new Set<string>();
        state.debouncedSearchResults = state.searchResults.filter((item: any) => {
          const itemName = item?.name?.trim().toLowerCase();
          if (itemName && !seenNames.has(itemName)) {
            seenNames.add(itemName);
            return true;
          }
          return false;
        });
      })
      .addCase(searchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchQuery,
  clearSearch,
  setSearchFilterOpen,
  setSelectedCategory,
  setSelectedCondition,
  setMaxDistance,
} = searchSlice.actions;
export const searchSelector = (state: { search: SearchState }) => state.search;
export default searchSlice.reducer;
